import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { useNotificationSound } from '../../utils/notificationSound';

const EMOJI_LIST = ['👍','❤️','😂','😮','😢','👏'];
const getAgentToken = () => {
  const t = localStorage.getItem('agentToken');
  return (!t || t === 'null' || t === 'undefined') ? null : t;
};
const authH = () => ({ headers: { Authorization: `Bearer ${getAgentToken()}` } });
const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : '';

function fileIcon(mt) {
  if (!mt) return '📎';
  if (mt.startsWith('image/')) return '🖼️';
  if (mt.startsWith('video/')) return '🎬';
  if (mt.startsWith('audio/')) return '🎵';
  if (mt.includes('pdf')) return '📄';
  return '📎';
}
function humanSize(b) {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return (b/1024).toFixed(1) + ' KB';
  return (b/1048576).toFixed(1) + ' MB';
}

function MsgBubble({ msg, myEmail, onDelete, onReact }) {
  const isMe = msg.senderEmail === myEmail || msg.senderRole === 'agent';
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef(null);

  useEffect(() => {
    if (!showEmoji) return;
    const h = (e) => { if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showEmoji]);

  if (msg.deletedAt) return <div style={{textAlign:isMe?'right':'left',padding:'4px 16px'}}><span style={{fontSize:12,color:'#9ca3af',fontStyle:'italic'}}>Message deleted</span></div>;

  const bubbleBg = isMe ? '#1a56db' : '#fff';
  const bubbleColor = isMe ? '#fff' : '#111827';
  const radius = isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px';

  const renderContent = () => {
    if (msg.fileUrl) {
      if (msg.type === 'image') return (
        <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer">
          <img src={msg.fileUrl} alt={msg.fileName||'Image'} style={{maxWidth:220,borderRadius:8,display:'block'}} onError={e=>{e.target.style.display='none';}}/>
          {msg.content && <div style={{fontSize:12,marginTop:4,opacity:.85}}>{msg.content}</div>}
        </a>
      );
      return (
        <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer"
          style={{display:'flex',alignItems:'center',gap:8,color:isMe?'#fff':'#1a56db',textDecoration:'none'}}>
          <span style={{fontSize:22}}>{fileIcon(msg.mimeType)}</span>
          <div>
            <div style={{fontWeight:600,maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{msg.fileName||'File'}</div>
            <div style={{fontSize:11,opacity:.7}}>{humanSize(msg.fileSize)}</div>
          </div>
        </a>
      );
    }
    return <span style={{wordBreak:'break-word'}}>{msg.content}</span>;
  };

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:isMe?'flex-end':'flex-start',marginBottom:8,padding:'0 12px'}}>
      {!isMe&&<div style={{fontSize:11,color:'#6b7280',marginBottom:2,marginLeft:4}}>Support</div>}
      <div style={{maxWidth:'70%',position:'relative'}}>
        <div onClick={()=>setShowEmoji(v=>!v)}
          style={{background:bubbleBg,color:bubbleColor,padding:'10px 14px',borderRadius:radius,fontSize:14,boxShadow:'0 1px 2px rgba(0,0,0,.08)',cursor:'pointer'}}>
          {renderContent()}
        </div>
        <div style={{fontSize:10,color:'#9ca3af',marginTop:2,textAlign:isMe?'right':'left'}}>{fmtTime(msg.timestamp)}</div>
        {msg.reactions&&Object.keys(msg.reactions).length>0&&(
          <div style={{display:'flex',gap:4,marginTop:4,flexWrap:'wrap'}}>
            {Object.entries(msg.reactions).map(([e,u])=><span key={e} onClick={()=>onReact(msg.messageId,e)} style={{cursor:'pointer',background:'#f3f4f6',borderRadius:12,padding:'2px 7px',fontSize:13,border:'1px solid #e5e7eb'}}>{e} {u.length}</span>)}
          </div>
        )}
        {showEmoji&&(
          <div ref={emojiRef} style={{position:'absolute',top:-46,right:isMe?0:'auto',left:isMe?'auto':0,background:'#fff',border:'1px solid #e5e7eb',borderRadius:20,padding:'6px 10px',display:'flex',gap:6,boxShadow:'0 4px 16px rgba(0,0,0,.15)',zIndex:20,alignItems:'center'}}>
            {EMOJI_LIST.map(e=><span key={e} onClick={ev=>{ev.stopPropagation();onReact(msg.messageId,e);setShowEmoji(false);}} style={{cursor:'pointer',fontSize:18,padding:'2px',lineHeight:1}}>{e}</span>)}
            {isMe&&<span onClick={ev=>{ev.stopPropagation();onDelete(msg.messageId);setShowEmoji(false);}} style={{cursor:'pointer',fontSize:15,padding:'2px 4px',color:'#ef4444',marginLeft:4,borderLeft:'1px solid #e5e7eb',paddingLeft:8}}>🗑</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function AgentSupport() {
  const [conv, setConv]         = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [uploadPct, setUploadPct] = useState(null);
  const bottomRef  = useRef(null);
  const socketRef  = useRef(null);
  const convIdRef  = useRef(null);
  const fileRef    = useRef(null);
  const playSound  = useNotificationSound();
  const myEmail = (() => { try { const p=JSON.parse(atob((localStorage.getItem('agentToken')||'').split('.')[1])); return p.memberEmail||p.email||''; } catch{return '';} })();

  const scrollDown = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

  // 1. Load / create conversation once
  useEffect(() => {
    if (!getAgentToken()) { setLoading(false); return; }
    axios.get('/api/support/agent/conversation', authH())
      .then(r => setConv(r.data.conversation))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // 2. Load messages when conversation is ready
  useEffect(() => {
    if (!conv) return;
    convIdRef.current = conv.conversationId;
    axios.get('/api/support/agent/conversation/messages', authH())
      .then(r => { setMessages((r.data.messages || []).slice().reverse()); scrollDown(); })
      .catch(console.error);
  }, [conv?.conversationId]);

  // 3. Socket — set up ONCE, use convIdRef to avoid stale closures
  useEffect(() => {
    const tok = getAgentToken();
    if (!tok) return; // no token → don't connect
    const s = io('/chat', { auth: { token: tok }, transports: ['websocket', 'polling'] });
    socketRef.current = s;

    s.on('message:new', ({ message }) => {
      if (!convIdRef.current || message.conversationId !== convIdRef.current) return;
      // Play sound for inbound messages (not sent by agent)
      if (message.senderRole !== 'agent') playSound();
      setMessages(prev => prev.find(m => m.messageId === message.messageId) ? prev : [...prev, message]);
      scrollDown();
    });
    s.on('message:deleted', ({ conversationId, messageId }) => {
      if (conversationId !== convIdRef.current) return;
      setMessages(prev => prev.map(m => m.messageId === messageId ? { ...m, deletedAt: new Date().toISOString() } : m));
    });
    s.on('message:reaction', ({ conversationId, messageId, reactions }) => {
      if (conversationId !== convIdRef.current) return;
      setMessages(prev => prev.map(m => m.messageId === messageId ? { ...m, reactions } : m));
    });

    return () => s.disconnect();
  }, []); // ← only once

  // 4. Join socket room whenever conversation becomes available
  useEffect(() => {
    if (!socketRef.current || !conv) return;
    convIdRef.current = conv.conversationId;
    socketRef.current.emit('join:conversation', { conversationId: conv.conversationId });
    return () => socketRef.current?.emit('leave:conversation', { conversationId: conv.conversationId });
  }, [conv?.conversationId]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const content = input.trim();
    setInput('');
    setSending(true);
    try {
      const res = await axios.post('/api/support/agent/conversation/messages', { content }, authH());
      // ✅ Optimistic append — don't wait for socket event
      const msg = res.data.message;
      if (msg) {
        setMessages(prev => prev.find(m => m.messageId === msg.messageId) ? prev : [...prev, msg]);
        scrollDown();
      }
    } catch(e) {
      console.error(e);
      setInput(content); // restore on failure
    } finally {
      setSending(false);
    }
  };

  const deleteMsg = async (mid) => {
    try { await axios.delete(`/api/support/agent/conversation/messages/${mid}`, authH()); }
    catch(e) { console.error(e); }
  };
  const reactMsg = async (mid, emoji) => {
    try { await axios.post(`/api/support/agent/conversation/messages/${mid}/react`, { emoji }, authH()); }
    catch(e) { console.error(e); }
  };

  const sendFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; e.target.value=''; setUploadPct(0);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(
        '/api/support/agent/conversation/upload', fd,
        { headers: { ...authH().headers }, onUploadProgress: ev => setUploadPct(Math.round(ev.loaded/ev.total*100)) }
      );
      setUploadPct(null);
      if (data.message) { setMessages(prev => prev.find(m=>m.messageId===data.message.messageId)?prev:[...prev,data.message]); scrollDown(); }
    } catch (err) { setUploadPct(null); console.error(err); alert('Upload failed'); }
  };

  if (loading) return <div style={{padding:40,textAlign:'center',color:'#9ca3af'}}>Loading…</div>;

  return (
    <div style={{display:'flex',flexDirection:'column',height:'calc(100vh - 80px)',background:'#f9fafb'}}>
      <div style={{padding:'14px 20px',background:'#fff',borderBottom:'1px solid #e5e7eb',display:'flex',alignItems:'center',gap:12}}>
        <div style={{width:38,height:38,borderRadius:'50%',background:'#1a56db',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:16}}>S</div>
        <div>
          <div style={{fontWeight:700,fontSize:15,color:'#111827'}}>Support</div>
          <div style={{fontSize:12,color:'#6b7280'}}>Ask us anything — we're here to help</div>
        </div>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'16px 0'}}>
        {messages.length===0&&(
          <div style={{textAlign:'center',padding:48,color:'#9ca3af'}}>
            <div style={{fontSize:40,marginBottom:12}}>👋</div>
            <div style={{fontSize:16,fontWeight:600,marginBottom:6}}>Welcome to Support</div>
            <div style={{fontSize:13}}>Send a message and we'll get back to you shortly.</div>
          </div>
        )}
        {messages.map(m=><MsgBubble key={m.messageId} msg={m} myEmail={myEmail} onDelete={deleteMsg} onReact={reactMsg}/>)}
        <div ref={bottomRef}/>
      </div>
      {uploadPct !== null && (
        <div style={{padding:'0 16px 6px'}}>
          <div style={{height:3,background:'#e5e7eb',borderRadius:2,overflow:'hidden'}}>
            <div style={{height:'100%',background:'#1a56db',width:`${uploadPct}%`,transition:'width .2s'}}/>
          </div>
        </div>
      )}
      <div style={{padding:'12px 16px',background:'#fff',borderTop:'1px solid #e5e7eb',display:'flex',gap:8,alignItems:'flex-end'}}>
        <input ref={fileRef} type="file" style={{display:'none'}} onChange={sendFile}/>
        <button onClick={()=>fileRef.current?.click()} title="Attach file"
          style={{padding:'10px 12px',background:'#f3f4f6',color:'#374151',border:'1px solid #e5e7eb',borderRadius:10,cursor:'pointer',fontSize:18,flexShrink:0}}>
          📎
        </button>
        <textarea value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendMessage();}}}
          placeholder="Type a message… (Enter to send)" rows={2}
          style={{flex:1,resize:'none',border:'1px solid #d1d5db',borderRadius:10,padding:'10px 14px',fontSize:14,fontFamily:'inherit',outline:'none'}}/>
        <button onClick={sendMessage} disabled={!input.trim()||sending}
          style={{padding:'10px 20px',background:input.trim()?'#1a56db':'#9ca3af',color:'#fff',border:'none',borderRadius:10,cursor:input.trim()?'pointer':'default',fontSize:14,fontWeight:600}}>
          {sending?'…':'Send'}
        </button>
      </div>
    </div>
  );
}
export default AgentSupport;
